import React, {useCallback, useEffect, useState} from 'react'
import {mapEmailNotificationSettingsToFormValues, mapFormValuesToRequestBody, OrderForm} from '../OrderForm'
import {editOrder} from 'services/OrderService'
import {SuccessDialog} from 'components/Shared/Dialogs/SuccessDialog'
import {HTTP_404_ERROR, HTTP_409_ERROR, HTTP_422_ERROR} from 'Constants'
import {useErrorHandling} from 'context/errorHandling'
import {
  DISPATCHER_NOT_FOUND,
  FORWARDER_NOT_FOUND,
  INVALID_DELIVERY_METHOD,
  INVALID_DELIVERY_POINTS
} from 'services/errorCodes'
import ApiError from 'constants/ApiError'
import {orderFormPointsOf} from '../formValues'
import {fetchOrderAndFormRelatedData} from '../dataFetch'
import {useNavigatePrevent} from 'context/navigatePrevent'
import {
  newTransactionFormValues
} from 'components/Order/OrderForms/OrderForm/OrderTransactionsForm/OrderTransactionsGroupForm/OrderTransactionForm/formValues'
import {
  TransactionType
} from 'components/Order/OrderForms/OrderForm/OrderTransactionsForm/OrderTransactionsGroupForm/OrderTransactionForm/TransactionType'
import {CarrierFormValues} from 'components/Order/OrderForms/OrderForm/CarrierForm'
import {ORDER_COST_READ, ORDER_INCOME_READ, useAuth} from 'context/auth'
import {extendOrderLock, setOrderLock} from "../../../../services";
import {ErrorDialog} from "../../../Shared/Dialogs";
import {useTranslation} from "react-i18next";

export function EditOrder(props) {
  const orderId = props.match.params.orderId
  const {handleError} = useErrorHandling()

  const [attachments, setAttachments] = useState(null)
  const [initialValues, setInitialValues] = useState(null)
  const [editedDialogOpen, setEditedDialogOpen] = useState(false)
  const [lockId, setLockId] = useState(null)
  const [lockedError, setLockedError] = useState(null)
  const [lockExtendingCount, setLockExtendingCount] = useState(0)

  const {setNavigationUnlocked} = useNavigatePrevent()
  const {hasAnyPrivileges} = useAuth()

  const getInitialFormValues = useCallback((order, ferryServices) => {
    const {
      attachments,
      loadingDeliveryPoints,
      unloadingDeliveryPoints,
      deliveryMethod,
      intermediatePoints,
      emailNotificationSettings,
      transactions,
      contractorInfo,
      ...rest
    } = order
    const points = orderFormPointsOf(loadingDeliveryPoints, unloadingDeliveryPoints,
      intermediatePoints, ferryServices)
    const orderIncomes = transactions?.filter(t => t.transactionType === TransactionType.Income)
    const orderCosts = transactions?.filter(t => t.transactionType === TransactionType.Cost)
    const carrier = CarrierFormValues.fromDeliveryMethod(deliveryMethod)
    const defaultPayerId = contractorInfo?.payerId
    const defaultCarrierId = carrier?.id

    const initialIncomes = hasAnyPrivileges([ORDER_INCOME_READ]) ? [newTransactionFormValues(TransactionType.Income, defaultPayerId)] : []
    const initialCosts = hasAnyPrivileges([ORDER_COST_READ]) ? [newTransactionFormValues(TransactionType.Cost, defaultCarrierId)] : []

    return {
      ...rest,
      contractorInfo,
      transactions: {
        incomes: orderIncomes.length > 0 ? orderIncomes : initialIncomes,
        costs: orderCosts.length > 0 ? orderCosts : initialCosts
      },
      attachments: attachments,
      deletedAttachmentsIds: [],
      carrier: carrier,
      points,
      emailNotificationSettings: mapEmailNotificationSettingsToFormValues(emailNotificationSettings)
    }
  }, [hasAnyPrivileges])

  useEffect(() => {
      if (!initialValues) {
        fetchOrderAndFormRelatedData({orderId, fetchForwarder: false, fetchDispatcher: false})
          .then(({order, ferryServices}) => {
            setAttachments(order.attachments)
            setInitialValues(getInitialFormValues(order, ferryServices))
          })
          .catch(error => handleError(error, {
              [HTTP_404_ERROR]: 'notifications.error.orderNotFound'
            }).then(() => {
              setNavigationUnlocked(history => history.push(`/orders`))
            })
          )
      }
    }, [orderId, initialValues, getInitialFormValues, handleError, setNavigationUnlocked]
  )

  useEffect(() => {
      if (orderId && lockExtendingCount === 0) {
        setOrderLock(orderId).then((response) => {
          setLockId(response.LockId)
          if (lockExtendingCount === 0) {
            setTimeout(() => {
              setLockExtendingCount(lockExtendingCount + 1)
            }, 60000)
          }
        }, (error) => {
          const errResp = error.response.data
          if (errResp.errorCode === 'RESOURCE_LOCKED') {
            setLockedError(errResp)
          } else {
            handleError(error)
          }
        })

      }

    }, [orderId, setLockId, setLockExtendingCount, lockExtendingCount, handleError]
  )

  useEffect(() => {
    let isComponentMounted = true;
    if (lockId && lockExtendingCount > 0) {
      extendOrderLock(orderId, lockId)
        .then((response) => {
          setLockId(response.LockId)
          if (isComponentMounted) {
            setTimeout(() => {
              if (isComponentMounted) {
                setLockExtendingCount(lockExtendingCount + 1)
              }
            }, 60000)
          }
        }, (error) => {
          const errResp = error.response.data
          if (errResp.errorCode === 'RESOURCE_LOCKED') {
            setLockedError(errResp)
          } else {
            handleError(error)
          }
        })
    }

    return () => {
      isComponentMounted = false;
    };
  }, [lockExtendingCount, setLockExtendingCount, lockId, orderId, handleError]);

  function handleSubmit(values, onSuccess) {
    const request = mapFormValuesToRequestBody(values, lockId)
    return editOrder(request)
      .then(() => onSuccess(orderId)
        .then(() => setEditedDialogOpen(true))
        .catch(handleError)
      )
      .catch(error => {
        const errorCode = error?.response?.data?.errorCode
        if (errorCode === 'RESOURCE_LOCKED') {
          setLockedError(error?.response?.data)
        } else {
          handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.editOrder',
            [HTTP_409_ERROR]: 'notifications.error.etagMismatch',
            [HTTP_422_ERROR]: errorCode === INVALID_DELIVERY_METHOD ?
              'notifications.error.invalidDeliveryMethod' :
              errorCode === INVALID_DELIVERY_POINTS ?
                'notifications.error.invalidDeliveryPoints' :
                errorCode === FORWARDER_NOT_FOUND ?
                  'notifications.error.forwarderNotFound' :
                  errorCode === DISPATCHER_NOT_FOUND ?
                    'notifications.error.dispatcherNotFound' :
                    ApiError.UnprocessableEntity
          })
        }
      })
  }

  return (attachments && initialValues && (
    <>
      <SuccessDialog open={editedDialogOpen}
                     onClose={() => {
                       setNavigationUnlocked(history => history.push(`/orders/details/${orderId}`))
                       setEditedDialogOpen(false)
                     }} content={'notifications.success.updatedOrder'}/>
      <ErrorDialog
        open={lockedError}
        onClose={() => {
          setNavigationUnlocked(history => history.push(`/orders/details/${orderId}`))
          setEditedDialogOpen(null)
        }}
        content={<OrderLockedInfoMessage error={lockedError}/>}
      />
      {lockId && <OrderForm initialValues={initialValues}
                            initialAttachments={attachments}
                            onSubmit={handleSubmit}
                            title={'form.order.edit'}
                            entityId={orderId}
                            lockId={lockId}>
      </OrderForm>}
    </>
  ))
}

export const OrderLockedInfoMessage = ({error}) => {
  const {t} = useTranslation()
  return (
    <>
      <p>{t('notifications.error.orderEditingLocked', {editingUser: error.data.user})}</p>
    </>
  )
}
